import { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/cn'

interface Props {
  value: string
  /** Display the value with this style. Defaults to monospace. */
  mono?: boolean
  className?: string
  /** Aria label for the copy button — defaults to "Copy <value>". */
  copyLabel?: string
  /** Toast message on success. Defaults to "Copied to clipboard". */
  toastMessage?: string
}

/**
 * AF-125 — copyable text field with Clipboard API + toast confirmation.
 * Used for the NIP session ID and NUBAN/account references.
 */
export function CopyableField({
  value,
  mono = true,
  className,
  copyLabel,
  toastMessage = 'Copied to clipboard',
}: Props) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      toast.success(toastMessage)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      toast.error('Could not copy to clipboard')
    }
  }

  return (
    <span className={cn('inline-flex items-center gap-1', className)}>
      <span className={cn('break-all', mono && 'font-mono text-xs')}>{value}</span>
      <button
        type="button"
        aria-label={copyLabel ?? `Copy ${value}`}
        onClick={() => void handleCopy()}
        className="rounded p-0.5 text-muted-fg transition-colors hover:bg-muted hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copied ? <Check size={12} className="text-status-success" /> : <Copy size={12} />}
      </button>
    </span>
  )
}
